import { placements } from '@popperjs/core'
import dynamic from 'next/dynamic'
import Link from 'next/link'
import { useRouter } from 'next/router'
import React, { ComponentType, useMemo } from 'react'
import { ContactSalesRepresentativesFilters } from '../pages/contactSalesRepresentatives'
import { useTranslate } from '../src/hooks/useTranslate'
import { VendorFragment, VendorType } from '../src/model/fragments'
import { getLocale } from '../src/model/locale'
import { getLinkWithQuery } from '../src/model/urls'
import { parseQueryInt } from '../src/utils/query'
import { CardBoxGrid } from './CardBoxGrid'
import { SearchResultWrapper } from './search/SearchResultWrapper'
import { VendorCard } from './vendors/VendorCard'
import { VendorLocationSearchBox } from './vendors/VendorLocationSearchBox'
import { VendorSearchBox } from './vendors/VendorSearchBox'
import { VendorsMap } from './vendors/VendorsMap'
import SvgBulb from './icons/SvgBulb'
import SvgCart from './icons/SvgCart'
import SvgWrench from './icons/SvgWrench'
import { MapSearch } from './MapSearch'
import { getPageQueryParams, Pagination } from './Pagination'
import { RadioButtons } from './RadioButtons'
import { WidthBound } from './WidthBound'

const MapContainer = dynamic(() => import('./MapContainer').then(mod => mod.MapContainer) as Promise<ComponentType>);

export interface SalesRepresentativesMapProps {
	contactPlaces: VendorFragment[]
	filter: ContactSalesRepresentativesFilters,
	onFilterChange: (f: ContactSalesRepresentativesFilters) => void
}

const contactPlaceTypeOrder = [
	VendorType.dealer,
	VendorType.store,
	VendorType.showroom,
]

const SalesRespresentativesMapLink: React.FC<{ page: number }> = ({ page, children }) => {
	const router = useRouter()
	const query = useMemo(() => getPageQueryParams(page), [page])
	const linkProps = React.useMemo(() => {
		return getLinkWithQuery(
			{
				as: router.asPath,
				href: {
					pathname: router.pathname,
					query: router.query,
				},
			},
			query,
		)
	}, [query, router.asPath, router.pathname, router.query])

	return <Link shallow={true} scroll={false} {...linkProps}>{children}</Link>
}

export const SalesRespresentativesMap: React.FunctionComponent<SalesRepresentativesMapProps> =
	({ contactPlaces, filter, onFilterChange }) => {
		const translate = useTranslate()
		const router = useRouter();

		const contactPlaceTypeButton = {
			[VendorType.dealer]: {
				text: translate('contact-place-dealer-label'),
				icon: <SvgCart style={{ transform: 'translateY(6%)' }} />,
			},
			[VendorType.store]: {
				text: translate('contact-place-store-label'),
				icon: <SvgWrench style={{ transform: 'translateY(6%)' }} />,
			},
			[VendorType.showroom]: {
				text: translate('contact-place-showroom-label'),
				icon: <SvgBulb style={{ transform: 'translateY(6%)' }} />,
			},
		}
		
		const [selectedPlace, setSelectedPlace] = React.useState<null | string>(
			null
		)

		const typeIndex = React.useMemo(() => {
			return filter.type ? (Object.keys(contactPlaceTypeButton).indexOf(filter.type) + 1) : 0;
		}, [filter]);

		const limit = 12;
		const [page, setPage] = React.useState<number>(1);
		React.useEffect(() => {
			router.events.on('routeChangeComplete', () => {
				setPage((router.query['page'] ? parseQueryInt(router.query['page']) : 1) ?? 1);
			});
		});

		const shownCards = React.useMemo(() => {
			if(!((filter.type && filter.type != VendorType.dealer) || filter.searchQuery || filter.address))
			{
				return [];
			}

			const offset = (page - 1) * limit;
			return contactPlaces.slice(offset, offset + limit);

		}, [page, filter, contactPlaces]);
		
		
		return (
			<div className="salesRespresentativesMap">
				<WidthBound>
					<RadioButtons 
						items={[
							{
								text: translate('salesRepresentatives-showAll-label'),
							},
							...contactPlaceTypeOrder.map((it) => contactPlaceTypeButton[it]),
						]}
						onChange={(index) => {
							onFilterChange({
								type: index ? Object.keys(contactPlaceTypeButton)[index - 1] as VendorType : null,
								address: filter.address,
								searchQuery: null
							});

							setPage(1);
						}}
						index={typeIndex}
						desiredIndex={typeIndex}
					/>
				</WidthBound>
				<MapContainer>
						<div className="salesRespresentativesMap-map">
							<VendorsMap
								filter={filter}
								contactPlaces={shownCards.length > 0 ? shownCards : contactPlaces}
								selectedPlace={selectedPlace}
								setSelectedPlace={setSelectedPlace}
							/>
						</div>
						<WidthBound>
							<MapSearch
								name={<VendorSearchBox currentQuery={filter.searchQuery} onSearch={(q) => {
									onFilterChange({
										type: null,
										address: filter.address,
										searchQuery: q
									});

									setPage(1);
								}} />}
								address={<VendorLocationSearchBox currentAddress={filter.address} onChangeAddress={addr => {
									onFilterChange({
										type: filter.type,
										address: addr,
										searchQuery: filter.searchQuery
									});

									setPage(1);
								}} />}
							/>
						</WidthBound>
						
						<WidthBound>
							<SearchResultWrapper hits={contactPlaces}>
							{
								shownCards.length > 0 && 
								<CardBoxGrid
									items={shownCards.map((place) => (
										<VendorCard onClick={() => setSelectedPlace(place.id)} place={place} />
									))}
								/>
							}

							
							{
								shownCards.length > 0 && contactPlaces.length > limit &&
								<Pagination
									LinkComponent={SalesRespresentativesMapLink}
									currentPage={page}
									pagesCount={Math.ceil(contactPlaces.length / (limit))}
								/>
							}
							</SearchResultWrapper>
						</WidthBound>

				</MapContainer>
			</div>
		)
	}
